import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  UNKNOWN_DATE_CANONICAL,
  appendUncertainDateSuffix,
  isUnknownDateValue,
  stripUncertainDateSuffix,
} from "@/lib/date-qualifiers";
import * as AF from "@/components/form-items/AbstractForm";
import { Checkbox as ShadCheckbox } from "@/components/ui/checkbox";
import { FormField, HeadlessFormItem } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useFieldLabelCustomized } from "@/context/FieldCustomizationOverrideContext";
import {
  useMaskableField,
  useRegisterVisibilityField,
} from "@/context/FormFieldVisibilityContext";
import { FieldVisibilityToggle } from "@/components/form-items/FieldVisibilityToggle";
import { useInsideMaskableGroup } from "@/components/form-items/CheckboxGroupMask";

export type DateQualifierFieldNames = {
  date: string;
  unknown: string;
  uncertain: string;
  estimated: string;
};

type DateQualifierFieldsProps = {
  names: DateQualifierFieldNames;
  dateClassName?: string;
  checkboxClassName?: string;
};

function QualifierCheckbox({
  name,
  checked,
  onCheckedChange,
  className,
}: {
  name: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const labelCustomized = useFieldLabelCustomized(name);
  const insideMaskableGroup = useInsideMaskableGroup();
  const { expanded, minimized, canToggle, toggleVisibility } = useMaskableField(
    name,
    { nonMaskable: insideMaskableGroup },
  );
  useRegisterVisibilityField(name, {
    nonMaskable: insideMaskableGroup,
    label: t(name),
    skip: insideMaskableGroup,
  });

  if (minimized) return null;

  return (
    <div className={cn("flex flex-col gap-1.5 w-full", className)}>
      <div className="flex flex-row items-center gap-2 w-full pt-0">
        <ShadCheckbox
          id={name}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
        <Label
          htmlFor={name}
          className={cn(
            "text-slate-700 w-full text-left text-sm font-medium font-['Raleway'] leading-normal flex items-center justify-between gap-2",
            labelCustomized && "italic",
          )}
        >
          <span className="min-w-0 whitespace-normal break-all">
            {t(name)}
          </span>
          {canToggle && !insideMaskableGroup && (
            <FieldVisibilityToggle
              expanded={expanded}
              minimized={minimized}
              onToggle={toggleVisibility}
            />
          )}
        </Label>
      </div>
    </div>
  );
}

/**
 * Champ date + Date inconnue / incertaine / estimée.
 * Mutue le texte (`s. d.` / ` ca.`) ; Inconnue ⊥ Incertaine ; saisie manuelle décoche tout.
 */
export function DateQualifierFields({
  names,
  dateClassName,
  checkboxClassName,
}: DateQualifierFieldsProps) {
  const form = useFormContext();
  const previousDateRef = useRef<string>("");
  const programmaticRef = useRef(false);
  const skipFirstDateWatch = useRef(true);

  const dateValue = useWatch({ control: form.control, name: names.date }) as
    | string
    | undefined;
  const unknown = !!useWatch({ control: form.control, name: names.unknown });
  const uncertain = !!useWatch({
    control: form.control,
    name: names.uncertain,
  });
  const estimated = !!useWatch({
    control: form.control,
    name: names.estimated,
  });

  useEffect(() => {
    const current = String(form.getValues(names.date) ?? "");
    if (!isUnknownDateValue(current) && current.trim() !== "") {
      previousDateRef.current = stripUncertainDateSuffix(current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Saisie manuelle → décoche les 3 options.
  useEffect(() => {
    if (skipFirstDateWatch.current) {
      skipFirstDateWatch.current = false;
      return;
    }
    if (programmaticRef.current) {
      programmaticRef.current = false;
      return;
    }
    if (!unknown && !uncertain && !estimated) return;
    form.setValue(names.unknown, false, { shouldDirty: true });
    form.setValue(names.uncertain, false, { shouldDirty: true });
    form.setValue(names.estimated, false, { shouldDirty: true });
  }, [dateValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const runProgrammatic = (fn: () => void) => {
    programmaticRef.current = true;
    fn();
  };

  const onUnknownChange = (checked: boolean) => {
    runProgrammatic(() => {
      if (checked) {
        const current = String(form.getValues(names.date) ?? "");
        if (!isUnknownDateValue(current) && current.trim() !== "") {
          previousDateRef.current = stripUncertainDateSuffix(current);
        }
        form.setValue(names.uncertain, false, { shouldDirty: true });
        form.setValue(names.unknown, true, { shouldDirty: true });
        form.setValue(names.date, UNKNOWN_DATE_CANONICAL, { shouldDirty: true });
      } else {
        form.setValue(names.unknown, false, { shouldDirty: true });
        form.setValue(names.date, previousDateRef.current || "", {
          shouldDirty: true,
        });
      }
    });
  };

  const onUncertainChange = (checked: boolean) => {
    runProgrammatic(() => {
      if (checked) {
        form.setValue(names.unknown, false, { shouldDirty: true });
        form.setValue(names.uncertain, true, { shouldDirty: true });
        let current = String(form.getValues(names.date) ?? "");
        if (isUnknownDateValue(current)) {
          current = previousDateRef.current || "";
        }
        form.setValue(names.date, appendUncertainDateSuffix(current), {
          shouldDirty: true,
        });
      } else {
        form.setValue(names.uncertain, false, { shouldDirty: true });
        const current = String(form.getValues(names.date) ?? "");
        form.setValue(names.date, stripUncertainDateSuffix(current), {
          shouldDirty: true,
        });
      }
    });
  };

  return (
    <AF.Col className="w-full gap-2">
      <AF.Text name={names.date} className={dateClassName} />
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full min-w-0">
        <FormField
          control={form.control}
          name={names.unknown}
          render={() => (
            <HeadlessFormItem>
              <QualifierCheckbox
                name={names.unknown}
                checked={unknown}
                onCheckedChange={onUnknownChange}
                className={checkboxClassName}
              />
            </HeadlessFormItem>
          )}
        />
        <FormField
          control={form.control}
          name={names.uncertain}
          render={() => (
            <HeadlessFormItem>
              <QualifierCheckbox
                name={names.uncertain}
                checked={uncertain}
                onCheckedChange={onUncertainChange}
                className={checkboxClassName}
              />
            </HeadlessFormItem>
          )}
        />
        <FormField
          control={form.control}
          name={names.estimated}
          render={({ field }) => (
            <HeadlessFormItem>
              <QualifierCheckbox
                name={names.estimated}
                checked={!!field.value}
                onCheckedChange={(checked) => field.onChange(checked)}
                className={checkboxClassName}
              />
            </HeadlessFormItem>
          )}
        />
      </div>
    </AF.Col>
  );
}

/** Réexport pour tests / import coerce côté BO si besoin. */
export { UNKNOWN_DATE_CANONICAL, isUnknownDateValue };
